import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaClock, FaArrowRight, FaTag } from 'react-icons/fa';

const BlogSlider = () => {
  // Sample blog data - this will be replaced with database data later
  const blogPosts = useMemo(() => [
    {
      id: 1,
      title: "Understanding Plantar Fasciitis: Causes and Treatment",
      excerpt: "Plantar fasciitis is one of the most common causes of heel pain. Learn about the causes, symptoms, and effective treatment options for this condition.",
      author: "Dr. Sarah Johnson",
      date: "2024-01-15",
      readTime: "5 min read",
      category: "Treatment",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop&crop=faces",
      tags: ["plantar fasciitis", "heel pain", "treatment"],
      featured: true
    },
    {
      id: 2,
      title: "The Importance of Proper Footwear for Foot Health",
      excerpt: "Discover how choosing the right shoes can prevent common foot problems and improve your overall foot health and comfort.",
      author: "Dr. Michael Chen",
      date: "2024-01-10",
      readTime: "4 min read",
      category: "Prevention",
      image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=800&h=400&fit=crop",
      tags: ["footwear", "prevention", "foot health"],
      featured: true
    },
    {
      id: 3,
      title: "Diabetic Foot Care: Essential Tips for Prevention",
      excerpt: "Learn essential foot care practices for diabetic patients to prevent complications and maintain healthy feet.",
      author: "Dr. Sarah Johnson",
      date: "2024-01-08",
      readTime: "7 min read",
      category: "Diabetic Care",
      image: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=800&h=400&fit=crop",
      tags: ["diabetes", "foot care", "prevention"],
      featured: true
    },
    {
      id: 4,
      title: "Sports Injuries: Common Foot and Ankle Problems in Athletes",
      excerpt: "Explore the most common foot and ankle injuries in sports and how to prevent and treat them effectively.",
      author: "Dr. Michael Chen",
      date: "2024-01-05",
      readTime: "6 min read",
      category: "Sports Medicine",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop",
      tags: ["sports", "injuries", "athletes"],
      featured: false
    }
  ], []);

  const [recentBlogs] = useState(blogPosts.filter(post => post.featured).slice(0, 3));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-primary/5 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-full opacity-30 blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-primary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-4"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <FaTag className="w-3 h-3" />
            Latest Articles
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Blog</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Stay informed with the latest insights, tips, and expert advice on foot health, 
            podiatry treatments, and maintaining healthy feet for life.
          </p>
        </motion.div>

        {/* Blog Grid */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {recentBlogs.map((blog, index) => (
            <motion.article 
              key={blog.id}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              className="group"
            >
              <Link to={`/blog/${blog.id}`} className="block">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 group-hover:border-primary/30">
                  {/* Blog Image */}
                  <div className="relative h-48 overflow-hidden">
                    <motion.img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      whileHover={{ scale: 1.1 }}
                    />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-gradient-to-r from-secondary to-primary text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                        {blog.category}
                      </span>
                    </div>

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    {/* Meta Information */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="w-3 h-3" />
                        <span>{formatDate(blog.date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FaClock className="w-3 h-3" />
                        <span>{blog.readTime}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-secondary transition-colors duration-300">
                      {blog.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {blog.excerpt}
                    </p>

                    {/* Author and Read More */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FaUser className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600 font-medium">{blog.author}</span>
                      </div>
                      
                      <motion.div 
                        className="flex items-center gap-1 text-secondary font-semibold text-sm group-hover:gap-2 transition-all duration-300"
                        whileHover={{ x: 5 }}
                      >
                        <span>Read More</span>
                        <FaArrowRight className="w-3 h-3" />
                      </motion.div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                      {blog.tags.slice(0, 2).map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs hover:bg-primary/10 hover:text-secondary transition-colors duration-200"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
              </div>
            </Link>
            </motion.article>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          <Link to="/blog">
            <motion.button
              className="inline-flex items-center gap-3 bg-gradient-to-r from-secondary to-primary text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 20px 40px rgba(190, 149, 196, 0.4)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span>View All Articles</span>
              <motion.div
                className="group-hover:translate-x-1 transition-transform duration-300"
              >
                <FaArrowRight className="w-4 h-4" />
              </motion.div>
            </motion.button>
          </Link>
        </motion.div>
    </div>
    </section>
  );
};

export default BlogSlider;